import { Check, Delete } from "@mui/icons-material";
import { alpha, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import PreviewItem from "./PreviewItem";
import { ExcelData } from "./utils";

interface PreviewStepProps {
    previewData: ExcelData[];
    loading: boolean;
    onStepChange: (step: number) => void;
    onSubmit: () => void;
    onPreviewDataChange: (data: ExcelData[]) => void;
}

const PreviewStep = ({
    previewData,
    loading,
    onStepChange,
    onSubmit,
    onPreviewDataChange
}: PreviewStepProps) => {
    const duplicatedUsers = previewData.filter((item) => item.duplicated);
    const nonDuplicatedUsers = previewData.filter((item) => !item.duplicated);

    const handleDeleteDuplicated = (index: number) => {
        const updatedData = [...previewData];
        updatedData.splice(index, 1);
        onPreviewDataChange(updatedData);
    };

    const handleOverrideDuplicated = (index: number) => {
        const updatedData = [...previewData];
        const item = updatedData[index];
        if (item) {
            delete item.duplicated;
            onPreviewDataChange(updatedData);
        }
    };

    return (
        <Stack gap={4}>
            {
                duplicatedUsers.length > 0 && (
                    <Stack gap={2}>
                        <Stack gap={1}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} >
                                <Typography variant="h6" sx={{ color: theme => theme.palette.error.main }}>Duplicated Users:</Typography>
                                <Typography
                                    component="span"
                                    sx={{
                                        bgcolor: theme => theme.palette.error.main,
                                        color: 'white',
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: 10,
                                        fontSize: '0.875rem',
                                        fontWeight: 500
                                    }}
                                >
                                    {duplicatedUsers.length} items
                                </Typography>
                            </Stack>
                            <Typography variant="caption" sx={{ color: theme => theme.palette.error.main }}>
                                These users already exist in the database, please choose how to handle them
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme => theme.palette.error.main }}>
                                **Empty fields will not be updated
                            </Typography>
                        </Stack>
                        {duplicatedUsers.map((item, index) => (
                            <Stack key={index} direction="row" alignItems="flex-start" width={1} gap={1}>
                                <PreviewItem item={item} cardProps={{ sx: { flexGrow: 1, border: theme => `1px solid ${alpha(theme.palette.error.main, 0.5)}` } }} />
                                <Stack height={60} width={40} justifyContent="center" alignItems="center">
                                    <Tooltip title="Drop the update of this user">
                                        <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() => handleDeleteDuplicated(previewData.indexOf(item))}
                                            sx={{
                                                height: 40,
                                                width: 40,
                                                bgcolor: theme => theme.palette.error.main,
                                                '&:hover': {
                                                    bgcolor: theme => alpha(theme.palette.error.main, 0.5)
                                                }
                                            }}
                                        >
                                            <Delete sx={{ color: theme => theme.palette.error.contrastText }} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                                <Stack height={60} width={40} justifyContent="center" alignItems="center">
                                    <Tooltip title="Override the existing user">
                                        <IconButton
                                            color="success"
                                            size="small"
                                            onClick={() => handleOverrideDuplicated(previewData.indexOf(item))}
                                            sx={{
                                                height: 40,
                                                width: 40,
                                                bgcolor: theme => theme.palette.success.main,
                                                '&:hover': {
                                                    bgcolor: theme => alpha(theme.palette.success.main, 0.5)
                                                }
                                            }}
                                        >
                                            <Check sx={{ color: theme => alpha(theme.palette.success.contrastText, 0.9) }} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        ))}
                    </Stack>
                )
            }

            <Stack gap={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                    <Typography variant="h6">Preview:</Typography>
                    <Typography
                        component="span"
                        sx={{
                            backgroundColor: 'primary.main',
                            color: 'white',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 10,
                            fontSize: '0.875rem',
                            fontWeight: 500
                        }}
                    >
                        {nonDuplicatedUsers.length} items
                    </Typography>
                </Stack>
                {nonDuplicatedUsers.map((item, index) => (
                    <PreviewItem key={index} item={item} />
                ))}

                <Stack alignItems="flex-end" mt={2}>
                    {
                        previewData.length === 0 && (
                            <Typography color="error.main" mb={1}>No data to import, please go back and change the file</Typography>
                        )
                    }
                    <Stack direction="row" gap={1}>
                        <Button variant="outlined" onClick={() => onStepChange(0)}>
                            Back
                        </Button>

                        <Tooltip
                            title={
                                duplicatedUsers.length > 0 ? "Duplicated users found, please config how to handle them" :
                                    nonDuplicatedUsers.length === 0 ? "No data to import, please go back and change the file" :
                                        "Confirm the import"
                            }
                            disableFocusListener={false}
                            disableHoverListener={false}
                            disableTouchListener={false}
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    onClick={onSubmit}
                                    disabled={loading || duplicatedUsers.length > 0 || nonDuplicatedUsers.length === 0}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Confirm Import'}
                                </Button>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
};

export default PreviewStep; 